import React, { useState } from "react";
import { Table, Modal, ModalBody } from "reactstrap";

function ruleDetails(rule) {
  if (!rule) return [];
  return [
    rule.unidade ? ["Unidade", rule.unidade] : null,
    rule.valorUtilizado != null ? ["Base", Number(rule.valorUtilizado).toLocaleString("pt-BR")] : null,
    rule.faixa ? ["Faixa", rule.faixa] : null,
    rule.faixaCep ? ["CEP", rule.faixaCep] : null,
  ].filter(Boolean);
}

function normalizeRow(row) {
  const componentNames = Array.isArray(row.componentNames) ? row.componentNames : [];
  const hasSimulado = row.nfesValor !== null && row.nfesValor !== undefined && row.nfesValor !== "";
  return {
    ...row,
    cteComponente: row.cteComponente || componentNames.join(" / ") || null,
    // Componente só do CTe (sem de/para) não tem item simulado: mostrar o label
    // no lugar fazia parecer que estava vinculado.
    simuladoItem: row.simuladoItem || (hasSimulado ? row.label : null) || null,
  };
}

function formatDifference(value, formatValue) {
  if (value === null || value === undefined || value === "") return "-";
  const numeric = Number(value);
  const formatted = formatValue(value, "currency");
  return Number.isFinite(numeric) && numeric > 0.005 ? `+${formatted}` : formatted;
}

function sumNumeric(items, field) {
  return items.reduce((total, item) => {
    const value = Number(item[field]);
    return Number.isFinite(value) ? total + value : total;
  }, 0);
}

function hasValue(value) {
  return value !== null && value !== undefined && value !== "";
}

// Pontas que podem ser ligadas por arrastar-e-soltar: componente só do CTe
// (sem de/para, um único ComponentePrestacaoServico) e taxa só do simulado
// (sem de/para, com a FreteTaxa conhecida). O vínculo criado é o de/para
// FreteTaxa <-> ComponentePrestacaoServico, o mesmo da tela Taxas de Frete.
function linkKind(item) {
  const hasCte = hasValue(item.cteValor);
  const hasSim = hasValue(item.nfesValor);
  if (hasCte && !hasSim && Array.isArray(item.componentIds) && item.componentIds.length === 1) return "cte";
  if (hasSim && !hasCte && item.idFreteTaxa) return "sim";
  return null;
}

const modalHeaderStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  background: "var(--everlog-card-hero-bg)",
  borderBottom: "1px solid var(--everlog-card-border)",
  padding: "16px 20px",
};

const modalTitleStyle = { color: "var(--everlog-card-title)", fontWeight: 600, fontSize: 15 };

const modalCloseStyle = {
  background: "none",
  border: "none",
  color: "var(--everlog-card-title)",
  fontSize: 22,
  lineHeight: 1,
  cursor: "pointer",
  padding: "0 4px",
};

const smallButtonStyle = {
  display: "inline-flex",
  alignItems: "center",
  height: 26,
  padding: "0 0.6rem",
  borderRadius: 999,
  border: "1px solid var(--everlog-info-button-border)",
  background: "transparent",
  color: "var(--everlog-status-info)",
  fontSize: "0.72rem",
  fontWeight: 700,
  cursor: "pointer",
  whiteSpace: "nowrap",
};

const primaryButtonStyle = {
  ...smallButtonStyle,
  height: 32,
  padding: "0 0.9rem",
  background: "var(--everlog-status-info)",
  color: "var(--everlog-on-accent-text, #fff)",
  border: "1px solid var(--everlog-status-info)",
};

const mutedTextStyle = { color: "var(--everlog-card-muted)", fontSize: "0.78rem" };

function ModalFrame({ isOpen, onClose, title, children, size }) {
  return (
    <Modal isOpen={isOpen} toggle={onClose} size={size || "md"} modalClassName="modal-align-top" fade={false}>
      <div style={modalHeaderStyle}>
        <span style={modalTitleStyle}>{title}</span>
        <button type="button" onClick={onClose} style={modalCloseStyle} aria-label="Fechar">
          ×
        </button>
      </div>
      <ModalBody style={{ maxHeight: "75vh", overflowY: "auto" }}>{children}</ModalBody>
    </Modal>
  );
}

function FormulaModal({ item, onClose }) {
  if (!item) return null;
  const formulas = Array.isArray(item.formulas) && item.formulas.length ? item.formulas : [item.formula].filter(Boolean);
  const details = ruleDetails(item.regra);

  return (
    <ModalFrame isOpen onClose={onClose} title={`Fórmula do simulador · ${item.simuladoItem || item.label || "item"}`}>
      {formulas.length ? (
        formulas.map((formula, index) => (
          <pre
            key={`${formula}_${index}`}
            style={{
              margin: index === formulas.length - 1 ? 0 : "0 0 0.5rem",
              padding: "0.65rem 0.8rem",
              borderRadius: 6,
              background: "var(--everlog-card-surface-field)",
              color: "var(--everlog-card-title)",
              fontSize: "0.85rem",
              whiteSpace: "pre-wrap",
              overflowWrap: "anywhere",
            }}
          >
            {formula}
          </pre>
        ))
      ) : (
        <div style={mutedTextStyle}>Sem memória de cálculo para este item.</div>
      )}
      {details.length ? (
        <dl style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: "0.25rem 0.9rem", margin: "0.9rem 0 0" }}>
          {details.map(([label, value]) => (
            <React.Fragment key={label}>
              <dt style={{ ...mutedTextStyle, fontWeight: 600 }}>{label}</dt>
              <dd style={{ margin: 0, color: "var(--everlog-card-title)", fontSize: "0.82rem", overflowWrap: "anywhere" }}>
                {value}
              </dd>
            </React.Fragment>
          ))}
        </dl>
      ) : null}
    </ModalFrame>
  );
}

function LinkConfirmModal({ link, busy, error, formatValue, onConfirm, onClose }) {
  if (!link) return null;
  const { cte, sim } = link;
  const taxaLabel = sim.codigoFreteTaxa ? `${sim.simuladoItem} (regra ${sim.codigoFreteTaxa})` : sim.simuladoItem;

  return (
    <ModalFrame isOpen onClose={busy ? undefined : onClose} title="Vincular componente à taxa">
      <div style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: "0.35rem 0.9rem", alignItems: "baseline" }}>
        <span style={mutedTextStyle}>Componente do CTe</span>
        <span style={{ color: "var(--everlog-card-title)", fontWeight: 700 }}>
          {cte.cteComponente} <span style={{ fontWeight: 400 }}>· {formatValue(cte.cteValor, "currency")}</span>
        </span>
        <span style={mutedTextStyle}>Taxa do simulador</span>
        <span style={{ color: "var(--everlog-card-title)", fontWeight: 700 }}>
          {taxaLabel} <span style={{ fontWeight: 400 }}>· {formatValue(sim.nfesValor, "currency")}</span>
        </span>
      </div>
      <p style={{ ...mutedTextStyle, margin: "0.9rem 0 0", lineHeight: 1.45 }}>
        O de/para é da taxa, não deste CTe: passa a valer em todas as tabelas de frete que usam a taxa{" "}
        <strong>{sim.simuladoItem}</strong>, de qualquer transportadora. É o mesmo vínculo da tela Taxas de Frete
        e pode ser desfeito lá. A auditoria deste CTe será recarregada.
      </p>
      {error ? (
        <div style={{ marginTop: "0.75rem", color: "var(--everlog-status-danger)", fontSize: "0.8rem" }}>{error}</div>
      ) : null}
      <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem", marginTop: "1rem" }}>
        <button type="button" style={{ ...smallButtonStyle, height: 32 }} onClick={onClose} disabled={busy}>
          Cancelar
        </button>
        <button type="button" style={{ ...primaryButtonStyle, opacity: busy ? 0.7 : 1 }} onClick={onConfirm} disabled={busy}>
          {busy ? "Vinculando..." : "Confirmar vínculo"}
        </button>
      </div>
    </ModalFrame>
  );
}

const FinancialComponentComparisonTable = ({
  rows,
  formatValue,
  headCellStyle,
  cellStyle,
  onLinkComponent,
}) => {
  const items = (Array.isArray(rows) ? rows : []).map(normalizeRow);
  const totalCte = sumNumeric(items, "cteValor");
  const totalSimulado = sumNumeric(items, "nfesValor");
  const totalDifference = totalSimulado - totalCte;
  const canLink = typeof onLinkComponent === "function";

  const [formulaItem, setFormulaItem] = useState(null);
  const [dragging, setDragging] = useState(null);
  const [dropTargetId, setDropTargetId] = useState(null);
  const [pendingLink, setPendingLink] = useState(null);
  const [linkBusy, setLinkBusy] = useState(false);
  const [linkError, setLinkError] = useState(null);

  const totalCellStyle = {
    ...cellStyle,
    borderTop: "2px solid var(--everlog-card-divider-strong)",
    paddingTop: "0.85rem",
    paddingBottom: "0.85rem",
    fontWeight: 800,
  };

  const handleDragStart = (event, item, kind) => {
    event.dataTransfer.effectAllowed = "link";
    event.dataTransfer.setData("text/plain", String(item.id));
    setDragging({ item, kind });
  };
  const handleDragEnd = () => {
    setDragging(null);
    setDropTargetId(null);
  };
  const isDropTarget = (kind) => Boolean(dragging) && dragging.kind !== kind;
  const handleDragOver = (event, item, kind) => {
    if (!isDropTarget(kind)) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = "link";
    if (dropTargetId !== item.id) setDropTargetId(item.id);
  };
  const handleDrop = (event, item, kind) => {
    if (!isDropTarget(kind)) return;
    event.preventDefault();
    const cte = kind === "cte" ? item : dragging.item;
    const sim = kind === "sim" ? item : dragging.item;
    setLinkError(null);
    setPendingLink({ cte, sim });
    handleDragEnd();
  };

  const closeLinkModal = () => {
    if (linkBusy) return;
    setPendingLink(null);
    setLinkError(null);
  };
  const confirmLink = async () => {
    if (!pendingLink || linkBusy) return;
    setLinkBusy(true);
    setLinkError(null);
    try {
      await onLinkComponent({
        idFreteTaxa: pendingLink.sim.idFreteTaxa,
        idComponentePrestacaoServico: Number(pendingLink.cte.componentIds[0]),
        componente: pendingLink.cte.cteComponente,
        taxa: pendingLink.sim.simuladoItem,
      });
      setPendingLink(null);
    } catch (error) {
      setLinkError(
        error?.response?.data?.error || error?.message || "Não foi possível criar o vínculo."
      );
    } finally {
      setLinkBusy(false);
    }
  };

  return (
    <>
      <Table size="sm" responsive style={{ marginBottom: 0 }}>
        <thead>
          <tr>
            <th style={headCellStyle}>Componente do CT-e</th>
            <th style={headCellStyle}>Cobrado</th>
            <th style={headCellStyle}>Item simulado</th>
            <th style={headCellStyle}>Fórmula</th>
            <th style={headCellStyle}>Calculado</th>
            <th style={headCellStyle}>Diferença</th>
          </tr>
        </thead>
        <tbody>
          {items.length ? items.map((item) => {
            const hasFormula = Boolean(item.formula) || (Array.isArray(item.formulas) && item.formulas.length > 0) || ruleDetails(item.regra).length > 0;
            const kind = canLink ? linkKind(item) : null;
            const draggable = Boolean(kind);
            const isTarget = draggable && isDropTarget(kind);
            const isHovered = isTarget && dropTargetId === item.id;
            const isSource = dragging && dragging.item.id === item.id;
            const rowStyle = isHovered
              ? { background: "var(--everlog-card-surface-active)", outline: "2px dashed var(--everlog-status-info)", outlineOffset: -2 }
              : isTarget
                ? { outline: "1px dashed var(--everlog-card-border)", outlineOffset: -1 }
                : undefined;
            const dragProps = draggable
              ? {
                  draggable: true,
                  onDragStart: (event) => handleDragStart(event, item, kind),
                  onDragEnd: handleDragEnd,
                  onDragOver: (event) => handleDragOver(event, item, kind),
                  onDragLeave: () => { if (dropTargetId === item.id) setDropTargetId(null); },
                  onDrop: (event) => handleDrop(event, item, kind),
                  title: kind === "cte"
                    ? "Componente sem de/para: arraste sobre a taxa simulada correspondente para vincular"
                    : "Taxa sem de/para: arraste sobre o componente do CTe correspondente para vincular",
                }
              : {};
            return (
              <tr key={item.id} style={{ ...rowStyle, cursor: draggable ? "grab" : undefined, opacity: isSource ? 0.55 : 1 }} {...dragProps}>
                <td style={{ ...cellStyle, fontWeight: 700 }}>
                  {item.cteComponente || "-"}
                  {kind === "cte" ? <span style={{ ...mutedTextStyle, marginLeft: "0.4rem" }} aria-hidden="true">⠿</span> : null}
                </td>
                <td style={{ ...cellStyle, whiteSpace: "nowrap" }}>{formatValue(item.cteValor, "currency")}</td>
                <td style={{ ...cellStyle, fontWeight: 700 }}>
                  {item.simuladoItem || "-"}
                  {kind === "sim" ? <span style={{ ...mutedTextStyle, marginLeft: "0.4rem" }} aria-hidden="true">⠿</span> : null}
                  {kind === "sim" && item.sugestaoComponente?.nome ? (
                    <div
                      title="O CTe tem um componente com o mesmo nome desta taxa, sem de/para. Arraste um sobre o outro para vincular."
                      style={{ ...mutedTextStyle, fontWeight: 400, fontSize: "0.7rem", marginTop: 2, cursor: "help" }}
                    >
                      mesmo nome no CTe: {item.sugestaoComponente.nome}
                    </div>
                  ) : null}
                </td>
                <td style={{ ...cellStyle, whiteSpace: "nowrap" }}>
                  {hasFormula ? (
                    <button type="button" style={smallButtonStyle} onClick={() => setFormulaItem(item)}>
                      Ver fórmula
                    </button>
                  ) : (
                    "-"
                  )}
                </td>
                <td style={{ ...cellStyle, whiteSpace: "nowrap" }}>{formatValue(item.nfesValor, "currency")}</td>
                <td style={{ ...cellStyle, whiteSpace: "nowrap" }}>{formatDifference(item.diferenca, formatValue)}</td>
              </tr>
            );
          }) : (
            <tr>
              <td colSpan="6" style={{ ...cellStyle, textAlign: "center", padding: "1rem" }}>
                Nenhum componente financeiro disponível para comparação.
              </td>
            </tr>
          )}
        </tbody>
        {items.length ? (
          <tfoot>
            <tr style={{ background: "var(--everlog-card-surface-active)" }}>
              <td style={totalCellStyle}>Total</td>
              <td style={{ ...totalCellStyle, whiteSpace: "nowrap" }}>{formatValue(totalCte, "currency")}</td>
              <td style={totalCellStyle}>Total</td>
              <td style={totalCellStyle}>-</td>
              <td style={{ ...totalCellStyle, whiteSpace: "nowrap" }}>{formatValue(totalSimulado, "currency")}</td>
              <td style={{ ...totalCellStyle, whiteSpace: "nowrap" }}>{formatDifference(totalDifference, formatValue)}</td>
            </tr>
          </tfoot>
        ) : null}
      </Table>
      {canLink && items.some((item) => linkKind(item)) ? (
        <div style={{ ...mutedTextStyle, marginTop: "0.6rem" }}>
          Itens marcados com ⠿ estão sem de/para: arraste o componente do CTe sobre a taxa simulada correspondente (ou o
          contrário) para criar o vínculo.
        </div>
      ) : null}

      <FormulaModal item={formulaItem} onClose={() => setFormulaItem(null)} />
      <LinkConfirmModal
        link={pendingLink}
        busy={linkBusy}
        error={linkError}
        formatValue={formatValue}
        onConfirm={confirmLink}
        onClose={closeLinkModal}
      />
    </>
  );
};

export default FinancialComponentComparisonTable;
